/**
 * SSE streaming client for the CoachWidget.
 * Uses fetch + ReadableStream (not EventSource — needs POST + cookies).
 */

export interface CoachRequest {
  sessionId: string | null;
  mode: "chat" | "essay_coach" | "counselor" | "assessment" | "admissions";
  message: string;
  workflow?: string;
  essayId?: string;
  currentDraft?: string;
  pageContext?: string;
}

export interface StreamCallbacks {
  onToken: (text: string) => void;
  onDone: (data: { sessionId: string; usage: { inputTokens: number; outputTokens: number } }) => void;
  onError: (error: string, code?: string) => void;
}

export function streamCoachMessage(body: CoachRequest, callbacks: StreamCallbacks): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch("/api/ai/coach", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Request failed" }));
        callbacks.onError(err.error || `HTTP ${res.status}`, err.code);
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) {
        callbacks.onError("No response stream");
        return;
      }

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events (split on double newline)
        const events = buffer.split("\n\n");
        buffer = events.pop() || ""; // last incomplete chunk stays in buffer

        for (const event of events) {
          const lines = event.split("\n");
          let eventType = "";
          let data = "";

          for (const line of lines) {
            if (line.startsWith("event: ")) eventType = line.slice(7);
            if (line.startsWith("data: ")) data = line.slice(6);
          }

          if (!eventType || !data) continue;

          try {
            const parsed = JSON.parse(data);
            if (eventType === "token" && parsed.t) {
              callbacks.onToken(parsed.t);
            } else if (eventType === "done") {
              callbacks.onDone(parsed);
            } else if (eventType === "error") {
              callbacks.onError(parsed.error, parsed.code);
            }
          } catch {
            // malformed event, skip
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return; // user cancelled
      callbacks.onError((err as Error).message || "Stream failed");
    }
  })();

  return controller;
}
